import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, Search } from 'lucide-react';

function CustomSelect(
  {
    label,
    name,
    value,
    error,
    onChange,
    options,
    placeholder = 'Select option',
    clearOptionLabel = '',
    disabled = false,
    searchable = true,
    leadingIcon: LeadingIcon,
    leadingIconClassName,
    wrapperClassName = 'relative grid w-full min-w-0 self-start gap-1.5',
    labelClassName = 'text-[0.74rem] font-medium text-[var(--theme-text-subtle)]',
    errorClassName = 'text-[0.74rem] text-[#bb5745]',
    triggerClassName = '',
    triggerShapeClassName = 'rounded-full',
    dropdownMinWidth = 0,
  },
  ref
) {
  const [isOpen, setIsOpen] = useState(false);
  const [placement, setPlacement] = useState('bottom');
  const [dropdownStyle, setDropdownStyle] = useState(null);
  const [query, setQuery] = useState('');
  const selectRef = useRef(null);
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const typeaheadQueryRef = useRef('');
  const typeaheadTimeoutRef = useRef(null);
  const selectedOption = options.find((option) => String(option.value) === String(value));
  const filteredOptions = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    if (!normalizedQuery) {
      return options;
    }

    return options.filter((option) =>
      `${option.label || ''} ${option.description || ''} ${option.badgeLabel || ''}`
        .toLowerCase()
        .includes(normalizedQuery)
    );
  }, [options, query]);

  useImperativeHandle(ref, () => triggerRef.current);

  useEffect(() => {
    function handlePointerDown(event) {
      const clickedTrigger = selectRef.current?.contains(event.target);
      const clickedDropdown = dropdownRef.current?.contains(event.target);

      if (!clickedTrigger && !clickedDropdown) {
        setIsOpen(false);
      }
    }

    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
      window.clearTimeout(typeaheadTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    function updatePlacement() {
      const triggerRect = triggerRef.current?.getBoundingClientRect();

      if (!triggerRect) {
        return;
      }

      const optionCount = Math.max(filteredOptions.length, 1);
      const searchHeight = searchable ? 42 : 0;
      const dropdownHeight = Math.min(292, 16 + searchHeight + optionCount * 38);
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
      const dropdownWidth = Math.max(triggerRect.width, Number(dropdownMinWidth) || 0);
      const spaceBelow = viewportHeight - triggerRect.bottom;
      const spaceAbove = triggerRect.top;
      const nextPlacement = spaceBelow < dropdownHeight && spaceAbove > spaceBelow
        ? 'top'
        : 'bottom';
      const dropdownTop = nextPlacement === 'top'
        ? Math.max(8, triggerRect.top - dropdownHeight - 8)
        : Math.min(triggerRect.bottom + 8, viewportHeight - 8);
      const dropdownLeft = Math.min(
        Math.max(8, triggerRect.left),
        Math.max(8, viewportWidth - dropdownWidth - 8)
      );

      setPlacement(nextPlacement);
      setDropdownStyle({
        left: `${dropdownLeft}px`,
        top: `${dropdownTop}px`,
        width: `${dropdownWidth}px`,
      });
    }

    updatePlacement();
    window.addEventListener('resize', updatePlacement);
    window.addEventListener('scroll', updatePlacement, true);

    return () => {
      window.removeEventListener('resize', updatePlacement);
      window.removeEventListener('scroll', updatePlacement, true);
    };
  }, [dropdownMinWidth, filteredOptions.length, isOpen, searchable]);

  function handleToggle() {
    if (disabled) {
      return;
    }

    setIsOpen((currentValue) => !currentValue);
  }

  function handleSelect(optionValue) {
    const selectedOption = options.find(
      (option) => String(option.value) === String(optionValue)
    );

    onChange({
      target: {
        name,
        value: optionValue,
      },
      option: selectedOption,
    });
    setQuery('');
    setIsOpen(false);
  }

  function handleTriggerKeyDown(event) {
    if (disabled) {
      return;
    }

    if (['Enter', 'ArrowDown', 'ArrowUp'].includes(event.key)) {
      event.preventDefault();
      setIsOpen(true);
      return;
    }

    if (!isTypeaheadKey(event)) {
      return;
    }

    event.preventDefault();
    const nextQuery = `${typeaheadQueryRef.current}${event.key}`.toLowerCase();
    let matchingQuery = nextQuery;
    let matchingOption = findTypeaheadOption(matchingQuery);

    if (!matchingOption) {
      matchingQuery = event.key.toLowerCase();
      matchingOption = findTypeaheadOption(matchingQuery);
    }

    typeaheadQueryRef.current = matchingQuery;
    window.clearTimeout(typeaheadTimeoutRef.current);
    typeaheadTimeoutRef.current = window.setTimeout(() => {
      typeaheadQueryRef.current = '';
    }, 900);

    if (matchingOption) {
      handleSelect(matchingOption.value);
    }
  }

  function findTypeaheadOption(searchText) {
    const normalizedSearchText = String(searchText || '').trim().toLowerCase();

    if (!normalizedSearchText) {
      return null;
    }

    return (
      options.find((option) =>
        String(option.label || option.value || '')
          .trim()
          .toLowerCase()
          .startsWith(normalizedSearchText)
      ) || null
    );
  }

  return (
    <div className={wrapperClassName} ref={selectRef}>
      {label ? (
        <span className={labelClassName}>{label}</span>
      ) : null}
      <button
        ref={triggerRef}
        className={`flex min-h-[40px] w-full min-w-0 items-center justify-between gap-2 ${triggerShapeClassName} border bg-white px-3 py-2 text-left text-[0.84rem] outline-none transition focus:ring-2 focus:ring-[var(--theme-accent-soft)] ${triggerClassName} ${
          error
            ? 'border-[#d78b7d] focus:border-[#c76552]'
            : 'border-[var(--theme-border)] focus:border-[var(--theme-accent)]'
        } ${
          disabled
            ? 'cursor-not-allowed bg-[#f3eee8] text-[#a9998b]'
            : selectedOption
              ? 'text-[var(--theme-text)]'
              : 'text-[var(--theme-text-muted)]'
        }`}
        type="button"
        onClick={handleToggle}
        onKeyDown={handleTriggerKeyDown}
        disabled={disabled}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        {LeadingIcon ? (
          <LeadingIcon
            className={leadingIconClassName || `h-4 w-4 shrink-0 ${error ? 'text-[#bb5745]' : 'text-[var(--theme-text-muted)]'}`}
            aria-hidden="true"
          />
        ) : null}
        <span className="min-w-0 flex-1 truncate text-left">{selectedOption?.label || placeholder}</span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-[var(--theme-accent)] transition ${
            isOpen ? 'rotate-180' : ''
          }`}
          aria-hidden="true"
        />
      </button>
      {error ? <span className={errorClassName}>{error}</span> : null}

      {isOpen && dropdownStyle
        ? createPortal(
            <div
              ref={dropdownRef}
              className="fixed z-[10000] min-w-0 rounded-[16px] border border-[var(--theme-border)] bg-white p-2 shadow-[0_18px_45px_rgba(var(--theme-shadow-rgb),0.16)]"
              style={dropdownStyle}
              data-placement={placement}
              data-custom-select-dropdown="true"
            >
              {searchable ? (
                <div className="relative mb-1.5">
                  <Search
                    className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--theme-text-muted)]"
                    aria-hidden="true"
                  />
                  <input
                    className="min-h-[34px] w-full rounded-[12px] border border-[var(--theme-border)] bg-white pl-9 pr-3 text-[0.82rem] outline-none placeholder:text-[var(--theme-text-muted)] focus:border-[var(--theme-accent)] focus:ring-2 focus:ring-[var(--theme-accent-soft)]"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="Search..."
                    autoFocus
                  />
                </div>
              ) : null}

              <div className="max-h-56 overflow-y-auto pr-1" role="listbox">
                {clearOptionLabel || filteredOptions.length ? (
                  <>
                    {clearOptionLabel ? (
                      <button
                        className={`flex min-h-[34px] w-full items-center justify-between gap-2 rounded-[12px] px-2.5 py-1.5 text-left text-[0.82rem] transition ${
                          !value
                            ? 'bg-[var(--theme-accent-soft)] font-semibold text-[var(--theme-text-strong)]'
                            : 'text-[var(--theme-text-strong)] hover:bg-[var(--theme-surface-soft)]'
                        }`}
                        type="button"
                        onClick={() => handleSelect('')}
                        role="option"
                        aria-selected={!value}
                      >
                        <span className="min-w-0 flex-1 truncate">{clearOptionLabel}</span>
                        {!value ? (
                          <Check className="h-4 w-4 shrink-0 text-[var(--theme-accent)]" aria-hidden="true" />
                        ) : null}
                      </button>
                    ) : null}
                    {filteredOptions.map((option) => {
                    const isSelected = String(option.value) === String(value);

                    return (
                      <button
                        key={option.value}
                        className={`flex min-h-[34px] w-full items-center justify-between gap-2 rounded-[12px] px-2.5 py-1.5 text-left text-[0.82rem] transition ${
                          isSelected
                            ? 'bg-[var(--theme-accent-soft)] font-semibold text-[var(--theme-text-strong)]'
                            : 'text-[var(--theme-text-strong)] hover:bg-[var(--theme-surface-soft)]'
                        }`}
                        type="button"
                        onClick={() => handleSelect(option.value)}
                        role="option"
                        aria-selected={isSelected}
                      >
                        <span className="min-w-0 flex-1 truncate">{option.label}</span>
                        <span className="ml-auto flex min-w-0 shrink-0 items-center justify-end gap-2">
                          {option.description ? (
                            <span
                              className="hidden max-w-[240px] truncate text-right text-[0.8rem] leading-4 text-[var(--theme-text-muted)] sm:inline"
                              title={option.description}
                            >
                              {option.description}
                            </span>
                          ) : null}
                          {option.badgeLabel ? (
                            <span
                              className={`inline-flex shrink-0 items-center rounded-full border px-2.5 py-0.5 text-[0.72rem] font-semibold ${
                                option.badgeTone === 'warning'
                                  ? 'border-[#e2b867] bg-[#fff8e8] text-[#93651a]'
                                  : option.badgeTone === 'success'
                                    ? 'border-[#9ccfa2] bg-[#edf8ef] text-[#2f7a3f]'
                                    : 'border-[var(--theme-border)] bg-[var(--theme-surface-muted)] text-[var(--theme-text-subtle)]'
                              }`}
                            >
                              {option.badgeLabel}
                            </span>
                          ) : null}
                          {isSelected ? (
                            <Check className="h-4 w-4 shrink-0 text-[var(--theme-accent)]" aria-hidden="true" />
                          ) : null}
                        </span>
                      </button>
                    );
                    })}
                  </>
                ) : (
                  <div className="px-3 py-5 text-center text-[0.8rem] text-[var(--theme-text-muted)]">
                    No results found
                  </div>
                )}
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
}

function isTypeaheadKey(event) {
  return (
    event.key.length === 1 &&
    !event.altKey &&
    !event.ctrlKey &&
    !event.metaKey &&
    !event.key.match(/\s/)
  );
}

export default forwardRef(CustomSelect);
